/**
 * Centralized API Client with automatic token refresh on 401
 * 
 * This utility eliminates duplicate token/refresh logic across services.
 * All API calls should use this client for consistent 401 handling.
 */

import axios from 'axios';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import authService from './authService';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

/**
 * Enhanced fetch with automatic 401 refresh-and-retry
 * 
 * @param url - API endpoint (relative or absolute)
 * @param options - Fetch options
 * @param retryOnce - Internal flag to prevent infinite retry loops
 * @returns Fetch response
 */
export async function fetchWithAuth(
  url: string,
  options: RequestInit = {},
  retryOnce = true
): Promise<Response> {
  // Build full URL if relative
  const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
  
  // Get current access token
  const token = authService.getAccessToken();
  
  // Add Authorization header if token exists
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(fullUrl, {
    ...options,
    headers,
  });

  // Handle 401 Unauthorized - attempt token refresh
  if (response.status === 401 && retryOnce) {
    console.log('[apiClient] 401 detected, attempting token refresh...');
    
    const refreshed = await authService.refreshToken();
    
    if (refreshed) {
      console.log('[apiClient] Token refreshed successfully, retrying request...');
      // Retry with new token (retryOnce=false prevents infinite loop)
      return fetchWithAuth(url, options, false);
    } else {
      console.error('[apiClient] Token refresh failed, clearing auth state');
      authService.clearAuthData();
      // Optional: Redirect to login
      // window.location.href = '/login';
    }
  }

  return response;
}

/**
 * Axios instance with automatic 401 refresh interceptor
 * 
 * Use this for services that prefer Axios over fetch
 */
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: Add auth token to all requests
axiosInstance.interceptors.request.use(
  (config) => {
    const token = authService.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: Handle 401 with token refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue requests while refresh is in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => axiosInstance(originalRequest))
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      console.log('[apiClient] 401 detected in Axios, attempting token refresh...');

      try {
        const refreshed = await authService.refreshToken();

        if (refreshed) {
          console.log('[apiClient] Token refreshed, retrying queued requests...');
          const token = authService.getAccessToken();
          processQueue(null, token);
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axiosInstance(originalRequest);
        } else {
          throw new Error('Token refresh failed');
        }
      } catch (refreshError) {
        console.error('[apiClient] Token refresh failed, clearing auth');
        processQueue(refreshError as Error, null);
        authService.clearAuthData();
        // Optional: Redirect to login
        // window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Ensure URL has /api prefix for backend routes
 */
function ensureApiPrefix(url: string): string {
  // Don't modify if already has /api prefix or is absolute URL
  if (url.startsWith('/api') || url.startsWith('http')) {
    return url;
  }
  // Add /api prefix for relative URLs
  return `/api${url.startsWith('/') ? '' : '/'}${url}`;
}

/**
 * Convenience methods for common HTTP operations
 */
export const apiClient = {
  /**
   * GET request with automatic auth and retry
   */
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return axiosInstance.get<T>(ensureApiPrefix(url), config);
  },

  /**
   * POST request with automatic auth and retry
   */
  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return axiosInstance.post<T>(ensureApiPrefix(url), data, config);
  },

  /**
   * PUT request with automatic auth and retry
   */
  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return axiosInstance.put<T>(ensureApiPrefix(url), data, config);
  },

  /**
   * PATCH request with automatic auth and retry
   */
  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return axiosInstance.patch<T>(ensureApiPrefix(url), data, config);
  },

  /**
   * DELETE request with automatic auth and retry
   */
  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return axiosInstance.delete<T>(ensureApiPrefix(url), config);
  },
};

export default apiClient;
